// SelahBrandMark
//
// The Selah brand identity — a rose-gold leaf with a slowly rotating sparkle.
// Extracted from Welcome.jsx and made reusable so the same warm animated mark
// appears on public pages (Welcome/Login/Register) and inside the authenticated
// shell (Dashboard sidebar, mobile header). Consistent identity across states.
//
// Props:
//   size      — leaf px size (default 40)
//   sparkle   — whether to render the rotating sparkle (default true)
//   sparkleSize — override sparkle icon size (defaults to size * 0.22)
//   className — wrapper class

import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';

const LeafSVG = ({ size = 40 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 120 120"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <defs>
      <linearGradient
        id={`leaf-gradient-${size}`}
        x1="0%"
        y1="0%"
        x2="100%"
        y2="100%"
      >
        <stop offset="0%" stopColor="#B87884" />
        <stop offset="100%" stopColor="#A76373" />
      </linearGradient>
    </defs>
    <path
      d="M60 20 C80 25, 90 45, 85 70 C80 95, 60 105, 40 95 C25 88, 20 70, 25 50 C30 30, 45 18, 60 20 Z"
      fill={`url(#leaf-gradient-${size})`}
      opacity="0.92"
    />
    <path
      d="M55 25 L60 85"
      stroke="#FFFFFF"
      strokeWidth="1"
      opacity="0.55"
      strokeLinecap="round"
    />
  </svg>
);

const SelahBrandMark = ({
  size = 40,
  sparkle = true,
  sparkleSize,
  className = '',
}) => {
  const iconSize = sparkleSize || Math.max(12, Math.round(size * 0.22));
  // Offset the sparkle just off the top-right corner of the leaf.
  const offset = Math.max(2, Math.round(size * 0.04));

  return (
    <div
      className={`relative inline-flex items-center justify-center flex-shrink-0 ${className}`}
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      <LeafSVG size={size} />
      {sparkle && (
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
          className="absolute"
          style={{ top: -offset, right: -offset }}
        >
          <Sparkles
            size={iconSize}
            className="text-[#B87884] drop-shadow-[0_0_4px_rgba(184,120,132,0.45)]"
          />
        </motion.div>
      )}
    </div>
  );
};

export default SelahBrandMark;
